const { isUserReload, shouldStartPassiveCheck } = require('./auth-flow');
const { currentLocalToken } = require('./auth-storage');

const ELEMENT_ID = 'sso-sync-curtain';

function isSsoCompletionLogin(search) {
	const params = new URLSearchParams(search || '');
	return params.get('sso_complete') === '1' || params.get('sso_active_complete') === '1';
}

function hasTerminalLoginMarker(search) {
	const params = new URLSearchParams(search || '');
	return params.has('sso_error') || params.has('sso_denied');
}

function matchRoute(routerObject, pathname) {
	if (!routerObject || typeof routerObject.match !== 'function') return null;
	try {
		return routerObject.match(pathname);
	} catch (error) {
		return null;
	}
}

function matchedRouteHasActiveSyncOwner(matchedRoute) {
	return !!matchedRoute
		&& Array.isArray(matchedRoute.matched)
		&& matchedRoute.matched.some(record => record
			&& record.meta
			&& record.meta.requiresAuth === true);
}

function routeHasActiveSyncOwner(routerObject, pathname) {
	return matchedRouteHasActiveSyncOwner(matchRoute(routerObject, pathname));
}

/**
 * Decide before Vue mounts whether an SSO journey needs one stable paint surface.
 * This never starts synchronization; the login page or protected-shell coordinator remains the
 * sole owner of that action.
 */
function shouldShowSsoSyncCurtainAtBoot({
	context,
	performanceObject = window.performance,
	locationObject = window.location,
	sessionStorage = window.sessionStorage,
	authStorage = window.localStorage,
	routerObject = null,
}) {
	if (!context || !context.enabled || !context.endpoints) return false;

	const pathname = locationObject.pathname;
	const search = locationObject.search || '';
	const matchedRoute = matchRoute(routerObject, pathname);
	const isLoginRoute = matchedRoute
		? matchedRoute.name === 'login'
		: pathname === context.endpoints.login_path;
	if (isLoginRoute) {
		if (isSsoCompletionLogin(search)) return true;
		if (hasTerminalLoginMarker(search)) return false;

		return shouldStartPassiveCheck({
			context,
			hasLocalSession: currentLocalToken(authStorage) !== null,
			search,
			storage: sessionStorage,
		});
	}

	return matchedRouteHasActiveSyncOwner(matchedRoute)
		&& context.active_sync_enabled
		&& currentLocalToken(authStorage) !== null
		&& isUserReload(performanceObject);
}

function hideSsoSyncCurtain(documentObject = document) {
	if (!documentObject || typeof documentObject.getElementById !== 'function') return;
	const element = documentObject.getElementById(ELEMENT_ID);
	if (element && typeof element.remove === 'function') element.remove();
}

function showSsoSyncCurtain(documentObject = document) {
	if (
		!documentObject
		|| typeof documentObject.getElementById !== 'function'
		|| typeof documentObject.createElement !== 'function'
		|| !documentObject.body
		|| typeof documentObject.body.append !== 'function'
		|| documentObject.getElementById(ELEMENT_ID)
	) return;

	const curtain = documentObject.createElement('div');
	curtain.id = ELEMENT_ID;
	curtain.setAttribute('role', 'status');
	curtain.setAttribute('aria-live', 'polite');
	curtain.setAttribute('aria-busy', 'true');
	curtain.setAttribute('data-sso-state', 'pending');
	curtain.style.cssText = [
		'position:fixed',
		'inset:0',
		'z-index:2147483647',
		'display:flex',
		'flex-direction:column',
		'align-items:center',
		'justify-content:center',
		'gap:16px',
		'background:#ffffff',
		'color:#4b5563',
		'font-family:system-ui,-apple-system,"Segoe UI","Hiragino Sans","Noto Sans JP",sans-serif',
		'font-size:14px',
	].join(';');

	const style = documentObject.createElement('style');
	style.textContent = '@keyframes sso-sync-curtain-spin{to{transform:rotate(360deg)}}';
	const spinner = documentObject.createElement('div');
	spinner.style.cssText = [
		'width:36px',
		'height:36px',
		'border-radius:50%',
		'border:3px solid rgba(0,0,0,0.12)',
		'border-top-color:#4b5563',
		'animation:sso-sync-curtain-spin 0.8s linear infinite',
	].join(';');
	const label = documentObject.createElement('div');
	label.textContent = 'ログイン状態を確認しています…';
	curtain.append(style, spinner, label);
	documentObject.body.append(curtain);
}

function showSsoSyncCurtainForBoot(context, routerObject) {
	if (shouldShowSsoSyncCurtainAtBoot({ context, routerObject })) showSsoSyncCurtain();
}

module.exports = {
	hideSsoSyncCurtain,
	routeHasActiveSyncOwner,
	shouldShowSsoSyncCurtainAtBoot,
	showSsoSyncCurtain,
	showSsoSyncCurtainForBoot,
};
